<template>
  <div>
    <div
      class="sticky-below-header !top-[--layout-offset-top] z-10 border-t bg-white py-5 shadow"
    >
      <app-page-wrapper>
        <catalog-inter-page-filters
          v-model="filters"
          :has-active-filters="hasActiveFilters"
          :number-of-active-filters="numberOfActiveFilters"
          :total-number-of-results="data.length"
          @reset="reset"
        />
      </app-page-wrapper>
    </div>

    <catalog-gradient-background
      class="pt-6 md:pt-12"
      :color="selectedSingleTag?.color"
    >
      <app-page-wrapper class="flex flex-col gap-16 md:gap-24">
        <div>
          <catalog-page-header
            class="mb-8 md:mb-16"
            :title="$t('catalog_inter.page.title')"
          >
            <template #subtitle>
              <span v-html="$t('catalog_inter.page.subtitle')" />
            </template>
          </catalog-page-header>

          <catalog-inter-page-no-results v-show="isEmpty" />

          <course-card-grid
            v-show="!isEmpty"
            id="catalog"
            :list="data"
            :loading="status === 'pending' && data.length === 0"
            show-next-session
          />
        </div>

        <catalog-missing-topic-contact-banner catalog-page-id="search" />

        <brand-customer-logos-banner />

        <brand-nps-banner>
          <template #append>
            <growth-contact-button
              color="dark"
              origin="catalog"
              origin_page_action="nps_banner_cta"
              origin_page_id="search"
              size="xl"
            />
          </template>
        </brand-nps-banner>

        <brand-qualiopi-banner />
      </app-page-wrapper>
    </catalog-gradient-background>
  </div>
</template>

<script lang="ts" setup>
import CatalogGradientBackground from "~/pages/catalog/_includes/components/catalog-gradient-background.vue";
import CatalogMissingTopicContactBanner from "~/pages/catalog/_includes/components/catalog-missing-topic-contact-banner.vue";
import CatalogPageHeader from "~/pages/catalog/_includes/components/catalog-page-header.vue";
import CatalogInterPageFilters from "~/pages/catalog/inter/_includes/components/catalog-inter-page-filters.vue";
import CatalogInterPageNoResults from "~/pages/catalog/inter/_includes/components/catalog-inter-page-no-results.vue";

definePageMeta({
  middleware(to) {
    if (!useFeatureFlag("catalog_v2"))
      return navigateTo(
        { name: "catalog-index-search", query: to.query },
        { redirectCode: 302 },
      );
  },
});

const { t } = useI18n();

useSeoMeta({
  title: t("catalog_inter.page.page_title"),
});

const { filters, hasActiveFilters, numberOfActiveFilters, reset } =
  useCatalogInterFilters();

const { data, status } = await useCourseList("catalog_next_sessions", filters, {
  lazy: true,
});

const isEmpty = computed(
  () => status.value === "success" && data.value.length === 0,
);

const { $analytics, $on } = useNuxtApp();

watch(
  filters,
  (value) => {
    document.documentElement.scrollTop = 0;
    $analytics.track("search_requested", value);
  },
  { deep: true },
);

if (import.meta.client) {
  $on("search_requested", (query: typeof filters) => {
    filters.tags = query.tags;
  });
}

const { data: tags } = useTagList();

const selectedSingleTag = computed(() => {
  if (filters.tags.length === 0 || filters.tags.length > 1) return;

  return tags.value.find((tag) => tag.slug === filters.tags[0]);
});
</script>
